import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Eye, EyeOff, Loader2, Shield, AlertCircle, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TrustTradeLogo from '../components/TrustTradeLogo';
import { trackCompleteRegistration, trackLead, trackSignUpClick } from '../utils/analytics';
import { safeAuthRedirect } from '../utils/authRedirect';

function getGoogleAuthUrl() {
  if (process.env.REACT_APP_GOOGLE_AUTH_URL) return process.env.REACT_APP_GOOGLE_AUTH_URL;
  return `${window.location.origin}/api/auth/google`;
}

function getSafeRedirect(search) {
  const params = new URLSearchParams(search || '');
  const queryRedirect = params.get('redirect');
  const storedRedirect = localStorage.getItem('intendedRoute') || sessionStorage.getItem('redirectAfterLogin');
  return safeAuthRedirect(queryRedirect || storedRedirect);
}

function clearStoredRedirect() {
  localStorage.removeItem('intendedRoute');
  sessionStorage.removeItem('redirectAfterLogin');
  sessionStorage.removeItem('pendingShareCode');
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    // Error from location state (internal navigation)
    if (location.state?.error) {
      setAuthError(location.state.error);
      toast.error(location.state.error);
      window.history.replaceState({}, document.title);
      return;
    }
    // Error from query param (redirect from backend OAuth failure)
    const params = new URLSearchParams(location.search);
    const qError = params.get('error');
    if (qError) {
      const msg = qError === 'google_auth_denied'
        ? 'Google sign-in was cancelled.'
        : 'Google sign-in failed. Please try again or use email.';
      setAuthError(msg);
      toast.error(msg);
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location.state, location.search]);

  useEffect(() => {
    if (isAuthenticated) {
      const destination = getSafeRedirect(location.search);
      clearStoredRedirect();
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, location.search]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {
      // Always show success per spec
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!isLoginMode) trackSignUpClick({ source: 'google_auth_button' });
    setGoogleLoading(true);
    setAuthError(null);
    const destination = getSafeRedirect(location.search);
    if (destination !== '/dashboard') {
      localStorage.setItem('intendedRoute', destination);
      sessionStorage.setItem('redirectAfterLogin', destination);
    }
    window.location.href = getGoogleAuthUrl();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoginMode) trackSignUpClick({ source: 'email_signup_form_submit' });
    setLoading(true);
    
    try {
      const endpoint = isLoginMode ? '/auth/login' : '/auth/register';
      const payload = isLoginMode 
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      
      const response = await api.post(endpoint, payload);

      if (!isLoginMode) {
        trackLead({
          method: 'email',
          requires_verification: Boolean(response.data.needs_verification),
        });
        trackCompleteRegistration({
          method: 'email',
          requires_verification: Boolean(response.data.needs_verification),
        });
      }

      if (response.data.needs_verification) {
        setRegistrationDone(true);
        setIsLoginMode(true);
        setForm(prev => ({ ...prev, password: '' }));
        return;
      }

      const token = response.data.session_token;
      const userData = {
        user_id: response.data.user_id,
        email: response.data.email,
        name: response.data.name,
        is_admin: response.data.is_admin || false,
      };

      login(userData, token);
      toast.success(isLoginMode ? 'Welcome back!' : 'Account created successfully!');
      const destination = getSafeRedirect(location.search);
      clearStoredRedirect();
      navigate(destination, { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Email not verified — offer a resend instead of a dead-end error.
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(detail.email || form.email);
        setAuthError(detail.message || 'Please verify your email before signing in.');
        return;
      }
      const msg = (typeof detail === 'string' ? detail : detail?.message) || 'Authentication failed';
      setAuthError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerification(true);
    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      toast.success('Verification email sent — please check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="tt-product min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-block mb-2">
            <TrustTradeLogo dark size="large" showText className="mx-auto" />
          </Link>
          <p className="text-sm text-muted-foreground">Secure Payments for South Africa</p>
        </div>

        <Card className="shadow-premium-lg">
          <CardHeader className="text-center pb-4 pt-6">
            <CardTitle className="text-xl font-bold">{isLoginMode ? 'Welcome back' : 'Create account'}</CardTitle>
            <CardDescription className="text-sm">
              {isLoginMode ? 'Sign in to manage your transactions' : 'Get started with secure payments'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-2 pb-6">
            {/* Google Sign-In */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 h-10 border border-input rounded-md bg-card hover:bg-accent transition-colors text-sm font-medium text-foreground mb-4 disabled:opacity-60"
            >
              {googleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              {googleLoading ? 'Redirecting…' : 'Continue with Google'}
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Forgot Password Panel */}
            {showForgotPassword && (
              <div className="mb-4 p-4 bg-muted/40 border border-border rounded-lg">
                {forgotSent ? (
                  <div className="text-center py-2">
                    <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Check your email</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      If an account exists with that email, you'll receive a reset link shortly.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                      className="mt-3 text-xs text-primary hover:text-primary/80 font-medium"
                    >
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-3">
                    <p className="text-sm font-medium text-foreground">Reset your password</p>
                    <p className="text-xs text-muted-foreground">Enter your email address and we'll send you a reset link.</p>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      required
                      className="h-9 text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(false)}
                        className="flex-1 text-xs text-muted-foreground hover:text-foreground py-2 border border-border rounded"
                      >
                        Cancel
                      </button>
                      <Button type="submit" disabled={forgotLoading} className="flex-1 h-8 text-xs">
                        {forgotLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send reset link'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLoginMode && (
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required={!isLoginMode}
                    className="h-10"
                    data-testid="register-name"
                  />
                </div>
              )}
              
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="h-10"
                  data-testid="login-email"
                />
              </div>
              
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">Password</Label>
                  {isLoginMode && (
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setForgotSent(false); }}
                      className="text-xs text-primary hover:text-primary/80"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={8}
                    className="h-10 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-10"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isLoginMode ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLoginMode ? 'Sign in' : 'Create account'
                )}
              </Button>
            </form>
            
            {/* Registration success */}
            {registrationDone && (
              <div className="mt-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Account created!</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Click the verification link we emailed you, then sign in to get started.</p>
                </div>
              </div>
            )}

            {/* Auth Error */}
            {authError && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-destructive">{authError}</p>
                  {unverifiedEmail && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="mt-2 text-sm font-semibold text-primary hover:text-primary/80 disabled:opacity-60"
                    >
                      {resendingVerification ? 'Sending…' : 'Resend verification email'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Toggle Mode */}
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => {
                  if (isLoginMode) trackSignUpClick({ source: 'login_mode_toggle' });
                  setIsLoginMode(!isLoginMode);
                }}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                {isLoginMode ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>

            {/* Trust Footer */}
            <div className="mt-5 pt-5 border-t border-border">
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5" />
                <span>Never share your password or one-time codes.</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-5 text-center text-xs text-muted-foreground">
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <span className="mx-2">•</span>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </div>
      </div>
    </div>
  );
}
